import {
  Controller,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import {
  SelectProps,
  TextFieldProps,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';

export type FormSelectOption = {
  label: string;
  value: string;
  color?: string;
  disabled?: boolean;
};

export type FormSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<SelectProps, 'variant'> &
  TextFieldProps & {
    control: UseControllerProps<TFieldValues, TName>['control'];
    name: UseControllerProps<TFieldValues, TName>['name'];
    options: FormSelectOption[];
    rules?: UseControllerProps<TFieldValues, TName>['rules'];
    fontSize?: string;
    loading?: boolean;
    emptyMessage?: string;
  };

export const FormSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: FormSelectProps<TFieldValues, TName>,
) => {
  const {
    control,
    options,
    rules,
    name,
    label,
    fontSize,
    disabled,
    loading,
    emptyMessage,
    sx,
    multiple,
    ...rest
  } = props;
  const theme = useTheme();
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({field, fieldState: {error}}) => {
        const selectedColor = options.find(
          option => option.value === field.value,
        )?.color;
        return (
          <FormControl
            variant="outlined"
            size="small"
            sx={[
              {minWidth: 155, width: '100%'},
              ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
            error={Boolean(error?.message)}
            disabled={disabled || loading}
          >
            {label && <InputLabel id={name}>{label}</InputLabel>}
            <Select
              labelId={name}
              id={name}
              fullWidth
              label={label}
              error={!!error}
              multiple={multiple}
              {...field}
              {...rest}
              onChange={e => {
                props.onChange?.(e, false);
                field.onChange(e);
              }}
              sx={{color: selectedColor, fontSize}}
              renderValue={selected =>
                options
                  .filter(option => {
                    if (multiple) {
                      return (selected as string[]).includes(option.value);
                    }
                    return selected === option.value;
                  })
                  .map(option => option.label)
                  .join(', ')
              }
            >
              {options.length > 0 &&
                options.map((option, idx) => (
                  <MenuItem
                    value={option.value}
                    key={idx}
                    sx={{color: option.color}}
                    disabled={option.disabled}
                  >
                    <Typography variant="inherit" noWrap>
                      {option.label}
                    </Typography>
                    {Array.isArray(field.value) &&
                      field.value.includes(option.value) && (
                        <ListItemIcon sx={{p: 0, minHeight: 'initial', ml: 1}}>
                          <CheckIcon
                            fontSize="small"
                            sx={{color: theme.palette.success.main}}
                          />
                        </ListItemIcon>
                      )}
                  </MenuItem>
                ))}
              {options.length === 0 && emptyMessage && (
                <Typography ml={2}>{emptyMessage}</Typography>
              )}
            </Select>
            {loading && (
              <LinearProgress
                sx={{
                  mt: '-2px',
                  mx: '1px',
                  height: '2px',
                  borderBottomLeftRadius: theme.tokens.radius.sm,
                  borderBottomRightRadius: '2px',
                }}
              />
            )}
            {error && (
              <Typography
                color={theme.palette.error.main}
                variant="body2"
                mt={0.5}
                ml={2}
              >
                {error.message}
              </Typography>
            )}
          </FormControl>
        );
      }}
    />
  );
};
